import React, { useState, createContext, useContext } from 'react';
type Language = 'en' | 'es';
interface TranslationContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string) => string;
}
const translations = {
  en: {
    // Navbar
    'nav.home': 'Home',
    'nav.howItWorks': 'How It Works',
    'nav.pricing': 'Pricing',
    'nav.contact': 'Contact',
    'nav.getStarted': 'Get Started',
    'nav.spanish': 'ESPAÑOL',
    // Hero
    'hero.badge': '#1 Done-For-You Credit Restoration',
    'hero.title': 'Professional Done-For-You Credit Restoration',
    'hero.subtitle': 'We fight the bureaus and creditors for you. Remove negative items and rebuild your financial future with Brixen Credit.',
    'hero.cta': 'Free Consultation',
    'hero.trusted': 'Trusted by thousands',
    'hero.trustpilot': 'Excellent on Trustpilot',
    'hero.dashboard.status': 'Credit Status',
    'hero.dashboard.protected': 'Protected',
    'hero.dashboard.active': 'Active',
    'hero.dashboard.yourScore': 'Your FICO Score',
    'hero.dashboard.excellent': 'Excellent',
    'hero.dashboard.negativeItems': 'Negative Items',
    'hero.dashboard.allRemoved': 'All Removed',
    'hero.dashboard.inquiries': 'Inquiries',
    'hero.dashboard.cleared': 'Cleared',
    'hero.dashboard.loanApproved': 'Loan Approved',
    'hero.dashboard.loanAmount': '$50,000 Personal Loan',
    'hero.dashboard.cardApproved': 'Credit Card Approved',
    'hero.dashboard.cardLimit': '$15,000 Credit Limit',
    'hero.dashboard.monitoring': '24/7 Monitoring',
    // Logo Slider
    'logoSlider.title': 'Trusted by Leading Credit Bureaus & Monitoring Services',
    // How It Works
    'howItWorks.title': 'How It Works',
    'howItWorks.subtitle': 'Our proven 3-step process has helped thousands restore their credit and achieve their financial goals.',
    'howItWorks.step1.title': 'Credit Analysis',
    'howItWorks.step1.desc': 'We review your credit reports from all three bureaus and identify every negative item affecting your score.',
    'howItWorks.step2.title': 'Dispute Strategy',
    'howItWorks.step2.desc': 'Our experts challenge inaccurate, unfair, and unverifiable items with bureaus and creditors on your behalf.',
    'howItWorks.step3.title': 'Results & Rebuilding',
    'howItWorks.step3.desc': 'Watch your score improve as negative items are removed. We guide you on building lasting credit health.',
    'howItWorks.cta': 'View Pricing Plans',
    // Pricing
    'pricing.title': 'Choose Your Credit Restoration Plan',
    'pricing.subtitle': 'Select the plan that fits your needs. All plans include our proven dispute process and dedicated support.',
    'pricing.mostPopular': 'Most Popular',
    'pricing.cta': 'Get Started',
    'pricing.note': 'All plans include a free initial consultation. No credit card required to start.',
    'pricing.perMonth': '/month',
    'pricing.oneTime': 'One-time payment',
    'pricing.goldFee': '$199 Initial Audit Fee',
    'pricing.platinumFee': '$299 Initial Audit Fee',
    'pricing.gold.feature1': 'Standard Restoration',
    'pricing.gold.feature2': 'Monthly Disputes',
    'pricing.gold.feature3': 'Credit Monitoring',
    'pricing.gold.feature4': 'Email Support',
    'pricing.gold.feature5': 'Progress Reports',
    'pricing.platinum.feature1': 'Aggressive Restoration',
    'pricing.platinum.feature2': 'Inquiry Removal',
    'pricing.platinum.feature3': 'Priority Disputes',
    'pricing.platinum.feature4': 'Phone Support',
    'pricing.platinum.feature5': 'Bi-Weekly Updates',
    'pricing.platinum.feature6': 'Creditor Interventions',
    'pricing.vip.feature1': 'Expedited Processing',
    'pricing.vip.feature2': '60-Day Turnaround',
    'pricing.vip.feature3': 'All Platinum Features',
    'pricing.vip.feature4': 'Dedicated Specialist',
    'pricing.vip.feature5': 'Priority Queue',
    'pricing.vipPlus.feature1': 'Priority Handling',
    'pricing.vipPlus.feature2': '45-Day Turnaround',
    'pricing.vipPlus.feature3': 'Business Funding Prep',
    'pricing.vipPlus.feature4': 'White Glove Service',
    'pricing.vipPlus.feature5': '1-on-1 Strategy Calls',
    'pricing.vipPlus.feature6': 'Credit Building Guidance',
    // Contact
    'contact.title': 'Start Your Journey to Better Credit',
    'contact.subtitle': 'Ready to take control of your financial future? Fill out the form and one of our credit specialists will contact you within 24 hours.',
    'contact.address': 'Office Address',
    'contact.phone': 'Phone',
    'contact.email': 'Email',
    'contact.hours': 'Hours of Operation',
    'contact.hoursValue': 'Monday - Saturday: 9:00 AM - 9:00 PM EST\nSunday: Closed',
    'contact.formTitle': 'Get Your Free Consultation',
    'contact.firstName': 'First Name',
    'contact.lastName': 'Last Name',
    'contact.emailLabel': 'Email',
    'contact.phoneLabel': 'Phone',
    'contact.plan': 'Select Your Plan',
    'contact.planPlaceholder': 'Choose a plan...',
    'contact.message': 'Additional Message (Optional)',
    'contact.messagePlaceholder': 'Tell us about your credit goals...',
    'contact.consent': 'I agree to the Terms and Conditions and Privacy Policy. BRIXEN CREDIT would like your consent to send text message communications from +1 (786) 627-9073 to your mobile number listed above, regarding customer care. Consent is not a condition of purchase. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe at any time. Reply HELP for assistance or more information.',
    'contact.submit': 'Request Call Back',
    'contact.successTitle': 'Request Received!',
    'contact.successMessage': 'Thank you for contacting us. A credit specialist will reach out to you within 24 hours.',
    'contact.submitAnother': 'Submit Another Request',
    'contact.consentRequired': 'Please accept the consent agreement to continue',
    // Footer
    'footer.description': 'Professional done-for-you credit restoration services. We fight for your financial future.',
    'footer.quickLinks': 'Quick Links',
    'footer.contact': 'Contact',
    'footer.hoursTitle': 'Hours of Operation',
    'footer.disclaimer': 'Brixen Credit is a credit restoration service. We are not a direct lender. This site is not affiliated with or endorsed by Facebook or Meta Platforms, Inc. "Facebook" and "Meta" are registered trademarks of Meta Platforms, Inc. Information shared on this website is for informational purposes only and does not constitute legal or financial advice. Results vary by individual profile. By using this site or our services, you agree to our Terms & Conditions and Privacy Policy, and consent to receive communications (including SMS and calls) from Brixen Credit under applicable laws.',
    'footer.copyright': '© 2026 Brixen Credit. All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms & Conditions'
  },
  es: {
    // Navbar
    'nav.home': 'Inicio',
    'nav.howItWorks': 'Cómo Funciona',
    'nav.pricing': 'Precios',
    'nav.contact': 'Contacto',
    'nav.getStarted': 'Comenzar',
    'nav.spanish': 'ENGLISH',
    // Hero
    'hero.badge': '#1 Restauración de Crédito Profesional',
    'hero.title': 'Restauración de Crédito Profesional Hecha Para Usted',
    'hero.subtitle': 'Luchamos contra las agencias y acreedores por usted. Elimine elementos negativos y reconstruya su futuro financiero con Brixen Credit.',
    'hero.cta': 'Consulta Gratuita',
    'hero.trusted': 'Confiado por miles',
    'hero.trustpilot': 'Excelente en Trustpilot',
    'hero.dashboard.status': 'Estado de Crédito',
    'hero.dashboard.protected': 'Protegido',
    'hero.dashboard.active': 'Activo',
    'hero.dashboard.yourScore': 'Su Puntaje FICO',
    'hero.dashboard.excellent': 'Excelente',
    'hero.dashboard.negativeItems': 'Elementos Negativos',
    'hero.dashboard.allRemoved': 'Todos Eliminados',
    'hero.dashboard.inquiries': 'Consultas',
    'hero.dashboard.cleared': 'Eliminadas',
    'hero.dashboard.loanApproved': 'Préstamo Aprobado',
    'hero.dashboard.loanAmount': '$50,000 Préstamo Personal',
    'hero.dashboard.cardApproved': 'Tarjeta de Crédito Aprobada',
    'hero.dashboard.cardLimit': '$15,000 Límite de Crédito',
    'hero.dashboard.monitoring': 'Monitoreo 24/7',
    // Logo Slider
    'logoSlider.title': 'Confiado por las Principales Agencias de Crédito y Servicios de Monitoreo',
    // How It Works
    'howItWorks.title': 'Cómo Funciona',
    'howItWorks.subtitle': 'Nuestro proceso probado de 3 pasos ha ayudado a miles a restaurar su crédito y alcanzar sus metas financieras.',
    'howItWorks.step1.title': 'Análisis de Crédito',
    'howItWorks.step1.desc': 'Revisamos sus reportes de crédito de las tres agencias e identificamos cada elemento negativo que afecta su puntaje.',
    'howItWorks.step2.title': 'Estrategia de Disputa',
    'howItWorks.step2.desc': 'Nuestros expertos desafían elementos inexactos, injustos y no verificables con las agencias y acreedores en su nombre.',
    'howItWorks.step3.title': 'Resultados y Reconstrucción',
    'howItWorks.step3.desc': 'Observe cómo mejora su puntaje a medida que se eliminan los elementos negativos. Le guiamos en la construcción de una salud crediticia duradera.',
    'howItWorks.cta': 'Ver Planes de Precios',
    // Pricing
    'pricing.title': 'Elija Su Plan de Restauración de Crédito',
    'pricing.subtitle': 'Seleccione el plan que se ajuste a sus necesidades. Todos los planes incluyen nuestro proceso de disputa probado y soporte dedicado.',
    'pricing.mostPopular': 'Más Popular',
    'pricing.cta': 'Comenzar',
    'pricing.note': 'Todos los planes incluyen una consulta inicial gratuita. No se requiere tarjeta de crédito para comenzar.',
    'pricing.perMonth': '/mes',
    'pricing.oneTime': 'Pago único',
    'pricing.goldFee': '$199 Tarifa de Auditoría Inicial',
    'pricing.platinumFee': '$299 Tarifa de Auditoría Inicial',
    'pricing.gold.feature1': 'Restauración Estándar',
    'pricing.gold.feature2': 'Disputas Mensuales',
    'pricing.gold.feature3': 'Monitoreo de Crédito',
    'pricing.gold.feature4': 'Soporte por Email',
    'pricing.gold.feature5': 'Reportes de Progreso',
    'pricing.platinum.feature1': 'Restauración Agresiva',
    'pricing.platinum.feature2': 'Eliminación de Consultas',
    'pricing.platinum.feature3': 'Disputas Prioritarias',
    'pricing.platinum.feature4': 'Soporte Telefónico',
    'pricing.platinum.feature5': 'Actualizaciones Quincenales',
    'pricing.platinum.feature6': 'Intervenciones con Acreedores',
    'pricing.vip.feature1': 'Procesamiento Acelerado',
    'pricing.vip.feature2': 'Entrega en 60 Días',
    'pricing.vip.feature3': 'Todas las Características Platinum',
    'pricing.vip.feature4': 'Especialista Dedicado',
    'pricing.vip.feature5': 'Cola Prioritaria',
    'pricing.vipPlus.feature1': 'Manejo Prioritario',
    'pricing.vipPlus.feature2': 'Entrega en 45 Días',
    'pricing.vipPlus.feature3': 'Preparación para Financiamiento Empresarial',
    'pricing.vipPlus.feature4': 'Servicio de Guante Blanco',
    'pricing.vipPlus.feature5': 'Llamadas de Estrategia 1-a-1',
    'pricing.vipPlus.feature6': 'Guía para Construir Crédito',
    // Contact
    'contact.title': 'Comience Su Viaje Hacia un Mejor Crédito',
    'contact.subtitle': '¿Listo para tomar control de su futuro financiero? Complete el formulario y uno de nuestros especialistas en crédito se comunicará con usted en 24 horas.',
    'contact.address': 'Dirección de Oficina',
    'contact.phone': 'Teléfono',
    'contact.email': 'Correo Electrónico',
    'contact.hours': 'Horario de Operación',
    'contact.hoursValue': 'Lunes - Sábado: 9:00 AM - 9:00 PM EST\nDomingo: Cerrado',
    'contact.formTitle': 'Obtenga Su Consulta Gratuita',
    'contact.firstName': 'Nombre',
    'contact.lastName': 'Apellido',
    'contact.emailLabel': 'Correo Electrónico',
    'contact.phoneLabel': 'Teléfono',
    'contact.plan': 'Seleccione Su Plan',
    'contact.planPlaceholder': 'Elija un plan...',
    'contact.message': 'Mensaje Adicional (Opcional)',
    'contact.messagePlaceholder': 'Cuéntenos sobre sus metas de crédito...',
    'contact.consent': 'Acepto los Términos y Condiciones y la Política de Privacidad. BRIXEN CREDIT desea su consentimiento para enviar comunicaciones de mensajes de texto desde +1 (786) 627-9073 al número de móvil que proporcionó arriba, con respecto al servicio al cliente. El consentimiento no es una condición de compra. La frecuencia de mensajes varía. Pueden aplicarse tarifas de mensajes y datos. Responda STOP para cancelar la suscripción en cualquier momento. Responda HELP para obtener asistencia o más información.',
    'contact.submit': 'Solicitar Llamada',
    'contact.successTitle': '¡Solicitud Recibida!',
    'contact.successMessage': 'Gracias por contactarnos. Un especialista en crédito se comunicará con usted en 24 horas.',
    'contact.submitAnother': 'Enviar Otra Solicitud',
    'contact.consentRequired': 'Por favor acepte el acuerdo de consentimiento para continuar',
    // Footer
    'footer.description': 'Servicios profesionales de restauración de crédito hechos para usted. Luchamos por su futuro financiero.',
    'footer.quickLinks': 'Enlaces Rápidos',
    'footer.contact': 'Contacto',
    'footer.hoursTitle': 'Horario de Operación',
    'footer.disclaimer': 'Brixen Credit es un servicio de restauración de crédito. No somos un prestamista directo. Este sitio no está afiliado ni respaldado por Facebook o Meta Platforms, Inc. "Facebook" y "Meta" son marcas registradas de Meta Platforms, Inc. La información compartida en este sitio web es solo para fines informativos y no constituye asesoramiento legal o financiero. Los resultados varían según el perfil individual. Al usar este sitio o nuestros servicios, acepta nuestros Términos y Condiciones y Política de Privacidad, y consiente recibir comunicaciones (incluidos SMS y llamadas) de Brixen Credit bajo las leyes aplicables.',
    'footer.copyright': '© 2026 Brixen Credit. Todos los derechos reservados.',
    'footer.privacy': 'Política de Privacidad',
    'footer.terms': 'Términos y Condiciones'
  }
};
const TranslationContext = createContext<TranslationContextType | undefined>(undefined);
export function TranslationProvider({
  children
}: {
  children: ReactNode;
}) {
  const [lang, setLang] = useState<Language>('es');
  const t = (key: string): string => {
    return translations[lang][key] || key;
  };
  return <TranslationContext.Provider value={{
    lang,
    setLang,
    t
  }} data-id="element-595">
      {children}
    </TranslationContext.Provider>;
}
export function useTranslations() {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslations must be used within a TranslationProvider');
  }
  return context;
}